import { ColumnsType } from 'antd/es/table';
import TitleBar from '../../../components/TitleBar';
import { Footer } from '../../../components/common/Footer';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Button } from '../../../components';
import { FETCH_PRODUCT_GROUP_VIEW } from '../queries';
import { useQuery } from '@apollo/client';
import FinancialData from '../../../components/common/FinancialData';
import TableV3 from '../../../components/generic/Table/TableV3';
import { tableSortingMethods } from '../../../utils';
import { useUserContext } from '../../../context/PermissionsContext';
import { getFilteredTags } from '../../../utils/getFilteredTags';
import QueryErrorBoundary from '../../../components/error-handling';
import { ExportColumn } from '../../../interfaces/ExportColumn';
import TitleUpdater from '../../../components/common/TitleUpdater';

type ProductGroup = {
  name: string;
  percentage: number;
  uuid: string;
};

type Location = {
  locationName: string;
  uuid: string;
};

type ProductGroupView = {
  groups: ProductGroup[];
  location: Location;
  totalRevenue: number;
};

interface DataReturned {
  groupView: ProductGroupView[];
}

interface TableData {
  locationName: string;
  groups: ProductGroup[];
  totalRevenue: number;
}

function ProductGroupView() {
  const navigate = useNavigate();
  const params = useParams();
  const divisionId = params.divisionId!;
  let [searchParams] = useSearchParams();

  const divisionName = searchParams.get('division') || '';

  const { data, loading, error } = useQuery<DataReturned>(
    FETCH_PRODUCT_GROUP_VIEW,
    {
      variables: {
        division: { uuid: divisionId },
      },
    },
  );

  const locationsData = data?.groupView || [];

  const { selectedTags, isLocation } = useUserContext();

  const filteredLocations = getFilteredTags({
    data: locationsData,
    selectedTags,
    condition: isLocation,
    lookUpKey: 'location.uuid',
  });

  const transformData = (data: DataReturned): TableData[] => {
    return data?.groupView?.map((groupView) => {
      return {
        locationName: groupView.location.locationName,
        groups: groupView.groups,
        totalRevenue: groupView.totalRevenue,
      };
    });
  };

  const groups = data?.groupView[0].groups || [];

  let sortedGroups = [...groups]?.sort(
    (a, b) =>
      (a.name.toLowerCase().includes('all other') ? 1 : -1) -
        (b.name.toLowerCase().includes('all other') ? 1 : -1) ||
      a.name.localeCompare(b.name),
  );

  const actualData = transformData({ groupView: filteredLocations });

  let groupColumns: ColumnsType<TableData> = sortedGroups?.map((group) => ({
    title: (
      <span className="flex items-center gap-4">
        {group.name}
        <br />
        <Button
          type={'link'}
          htmlType={'button'}
          onClick={() =>
            navigate(
              `/${divisionId}/product-group-view/${group.uuid}/product-view?division=${divisionName}&productGroup=${group.name}`,
            )
          }
          className="text-xsm"
        >
          drill
        </Button>
      </span>
    ),
    dataIndex: 'groups',
    key: group.uuid,
    width: 130,
    align: 'right',
    render: (_, record) => (
      <span className="custom-table--column">
        {record.groups
          .find((grp) => grp.uuid === group.uuid)
          ?.percentage.toFixed(0) || 0}
        {'%'}
      </span>
    ),
  }));

  const columns: ColumnsType<TableData> = [
    {
      title: 'Location',
      dataIndex: 'locationName',
      key: 'locationName',
      width: 250,
      fixed: 'left',
      sorter: (a, b) =>
        tableSortingMethods.string(a.locationName, b.locationName),
      render: (text: string) => (
        <span className="custom-table--column">{text}</span>
      ),
    },
    ...groupColumns,
    {
      title: 'Total Revenue Supported  Annually',
      dataIndex: 'totalRevenue',
      key: 'totalRevenue',
      width: 150,
      fixed: 'right',
      align: 'right',
      sorter: (a, b) =>
        tableSortingMethods.number(a.totalRevenue, b.totalRevenue),
      render: (text: number) => (
        <span className="custom-table--column custom-table--column-revenue">
          {<FinancialData text={text} />}
        </span>
      ),
    },
  ];

  const baseColumnsProductGroup: ExportColumn[] = [
    { key: 'locationName', header: 'Location', type: 'text', width: 45 },
    ...(actualData[0]?.groups?.map((group: any, index) => ({
      key: group.uuid,
      header: group.name,
      type: 'percentage' as const,
      nestedKey: `groups.${index}.percentage`,
      width: 45,
    })) || []),

    {
      key: 'totalRevenue',
      header: 'Total Revenue Supported  Annually',
      type: 'money',
      width: 45,
    },
  ];

  return (
    <div>
      <TitleUpdater
        title={`${divisionName} Division/Product Group Dependency Matrix`}
      />
      <TitleBar
        title="Dependency Matrix"
        brief="This matrix shows the percentage that each of your locations supports across the business
                      at various levels of granularity."
      />
      <div className="flex flex-col flex-nowrap justify-center items-center gap-6">
        <Button
          type="primary"
          className="text-sm"
          onClick={() => navigate('/division-view')}
        >
          Roll-Up
        </Button>
        <QueryErrorBoundary error={error}>
          <TableV3
            title={`${divisionName} // Product Group View`}
            columns={columns}
            data={actualData}
            loading={loading}
            customStyles="custom-column-PA--heading custom-table--total-revenue"
            exportExcelColumns={baseColumnsProductGroup}
          />
        </QueryErrorBoundary>
      </div>
      <Footer backPath={'/division-view'} />
    </div>
  );
}

export default ProductGroupView;
